<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import StatusTag from 'dashboard/components-next/tag/StatusTag.vue';
import BuyerProfileTag from './BuyerProfileTag.vue';
import VisitorInfoHub from './VisitorInfoHub.vue';
import {
  STAGE_META,
  formatDate,
  leadContactLine,
  leadInitials,
  leadStats,
  resolveLeadStage,
} from '../utils/crmPageUtils';
import { activeBusinessContextFromLead as visitorBusinessContextFromLead } from '../utils/visitorInfoPresenter';

const props = defineProps({
  show: { type: Boolean, default: false },
  lead: { type: Object, default: null },
  syncingCrm: { type: Boolean, default: false },
});

const emit = defineEmits([
  'close',
  'open-history',
  'create-referral',
  'schedule-visit',
  'message',
  'call',
  'sync-crm',
]);

const { t } = useI18n();

const stageKey = computed(() => resolveLeadStage(props.lead || {}));
const stageMeta = computed(
  () => STAGE_META[stageKey.value] || STAGE_META.sem_visita
);
const stageLabels = computed(() => ({
  sem_visita: t('LEADS.PIPELINE.STAGES.NO_VISIT'),
  uma_ou_duas_visitas: t('LEADS.PIPELINE.STAGES.ONE_TWO_VISITS'),
  tres_ou_mais_visitas: t('LEADS.PIPELINE.STAGES.THREE_PLUS_VISITS'),
  proposta_em_andamento: t('LEADS.PIPELINE.STAGES.OFFER'),
  negocio_fechado: t('LEADS.PIPELINE.STAGES.CLOSED'),
}));
const stageDescriptions = computed(() => ({
  sem_visita: t('LEADS.PIPELINE.STAGE_DESCRIPTIONS.NO_VISIT'),
  uma_ou_duas_visitas: t('LEADS.PIPELINE.STAGE_DESCRIPTIONS.ONE_TWO_VISITS'),
  tres_ou_mais_visitas: t(
    'LEADS.PIPELINE.STAGE_DESCRIPTIONS.THREE_PLUS_VISITS'
  ),
  proposta_em_andamento: t('LEADS.PIPELINE.STAGE_DESCRIPTIONS.OFFER'),
  negocio_fechado: t('LEADS.PIPELINE.STAGE_DESCRIPTIONS.CLOSED'),
}));
const stageLabel = computed(
  () => stageLabels.value[stageKey.value] || stageLabels.value.sem_visita
);
const stageDescription = computed(
  () =>
    stageDescriptions.value[stageKey.value] ||
    stageDescriptions.value.sem_visita
);
const stats = computed(() => leadStats(props.lead || {}));
const profile = computed(() => props.lead?.buyer_profile || null);
const contactLine = computed(() => leadContactLine(props.lead));
const lastActivity = computed(() => formatDate(stats.value.lastActivity));
const clientSince = computed(() =>
  formatDate(
    props.lead?.created_at ||
      props.lead?.createdAt ||
      props.lead?.first_contact_at ||
      props.lead?.additional_attributes?.first_contact_at
  )
);
const scheduledVisits = computed(
  () =>
    props.lead?.scheduled_visits_count ||
    props.lead?.lais_crm?.stats?.scheduled_visits_count ||
    props.lead?.additional_attributes?.lais_crm?.stats
      ?.scheduled_visits_count ||
    0
);
const lastVisit = computed(() =>
  formatDate(
    props.lead?.last_visit_at ||
      props.lead?.lais_crm?.stats?.last_visit_at ||
      props.lead?.additional_attributes?.lais_crm?.stats?.last_visit_at
  )
);
const activityCards = computed(() => [
  {
    icon: 'i-lucide-circle-check',
    label: t('LEADS.QUICK_VIEW.ACTIVITY.DONE_VISITS'),
    value: stats.value.visits,
  },
  {
    icon: 'i-lucide-calendar-check',
    label: t('LEADS.QUICK_VIEW.ACTIVITY.SCHEDULED_VISITS'),
    value: scheduledVisits.value,
  },
  {
    icon: 'i-lucide-clock-3',
    label: t('LEADS.QUICK_VIEW.ACTIVITY.LAST_VISIT'),
    value: lastVisit.value || t('LEADS.QUICK_VIEW.NO_ACTIVITY'),
  },
  {
    icon: 'i-lucide-file-signature',
    label: t('LEADS.QUICK_VIEW.ACTIVITY.OFFERS'),
    value: stats.value.offers,
  },
]);
const leadBusinessContext = computed(() =>
  visitorBusinessContextFromLead(props.lead || {})
);
const attendanceWindow = computed(() => props.lead?.attendance_window || null);
const crmSync = computed(() => props.lead?.crm_sync || {});
const crmAttempts = computed(() => props.lead?.crm_sync_attempts || []);
const latestCrmAttempt = computed(() => crmAttempts.value[0] || null);
const contactability = computed(() => props.lead?.contactability || {});
const leadAdditionalAttributes = computed(
  () => props.lead?.additional_attributes || {}
);
const qualificationQuestions = computed(
  () =>
    props.lead?.qualification_questions ||
    props.lead?.lais_crm?.qualification_questions ||
    {}
);
const qualificationQuestionItems = computed(
  () => qualificationQuestions.value.questions || []
);
const pendingQualifyingQuestions = computed(
  () => qualificationQuestions.value.pending_qualifying_questions || []
);
const CRM_STATUS_META = {
  queued: { labelKey: 'QUEUED', cls: 'bg-[#f5eef9] text-[#49317a]' },
  processing: { labelKey: 'PROCESSING', cls: 'bg-sky-50 text-sky-700' },
  synced: { labelKey: 'SYNCED', cls: 'bg-emerald-50 text-emerald-700' },
  failed: { labelKey: 'FAILED', cls: 'bg-rose-50 text-rose-700' },
  skipped: { labelKey: 'SKIPPED', cls: 'bg-slate-100 text-slate-700' },
  not_ready: { labelKey: 'NOT_READY', cls: 'bg-[#f5f5f5] text-[#4a5568]' },
};
const crmStatusMeta = computed(
  () => CRM_STATUS_META[crmSync.value.status] || CRM_STATUS_META.not_ready
);
const crmStatusLabels = computed(() => ({
  QUEUED: t('LEADS.QUICK_VIEW.CRM.STATUS.QUEUED'),
  PROCESSING: t('LEADS.QUICK_VIEW.CRM.STATUS.PROCESSING'),
  SYNCED: t('LEADS.QUICK_VIEW.CRM.STATUS.SYNCED'),
  FAILED: t('LEADS.QUICK_VIEW.CRM.STATUS.FAILED'),
  SKIPPED: t('LEADS.QUICK_VIEW.CRM.STATUS.SKIPPED'),
  NOT_READY: t('LEADS.QUICK_VIEW.CRM.STATUS.NOT_READY'),
}));
const crmStatusLabel = computed(
  () => crmStatusLabels.value[crmStatusMeta.value.labelKey]
);
const latestCrmAttemptLabel = computed(() =>
  latestCrmAttempt.value
    ? `#${latestCrmAttempt.value.attempt_number}`
    : t('LEADS.QUICK_VIEW.CRM.NO_ATTEMPT')
);
const latestCrmAttemptJson = computed(() =>
  JSON.stringify(
    {
      payload: latestCrmAttempt.value?.payload,
      response: latestCrmAttempt.value?.response,
    },
    null,
    2
  )
);
const isCrmSyncing = computed(
  () =>
    props.syncingCrm || ['queued', 'processing'].includes(crmSync.value.status)
);
const normalizeBypassValue = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_');
const crmBypassReason = computed(
  () =>
    props.lead?.lais_bypass_reason ||
    leadAdditionalAttributes.value.lais_bypass_reason ||
    crmSync.value.bypass_reason ||
    crmSync.value.bypass_origin
);
const hasIndicationBypass = computed(() => {
  const value = normalizeBypassValue(crmBypassReason.value);
  return value.includes('indicacao_na_mao') || value.includes('chaves_na_mao');
});
const crmBypassNotice = computed(
  () =>
    props.lead?.lais_bypass_notice ||
    leadAdditionalAttributes.value.lais_bypass_notice ||
    crmSync.value.bypass_notice ||
    t('LEADS.QUICK_VIEW.CRM.BYPASS_NOTICE')
);
const attendanceWindowStatus = computed(() => {
  if (!attendanceWindow.value) return null;
  if (attendanceWindow.value.meterable === false) {
    return t('LEADS.QUICK_VIEW.ATTENDANCE_WINDOW_NO_CHARGE');
  }
  return attendanceWindow.value.status === 'open'
    ? t('LEADS.QUICK_VIEW.ATTENDANCE_WINDOW_OPEN')
    : t('LEADS.QUICK_VIEW.ATTENDANCE_WINDOW_CLOSED');
});
const attendanceWindowMeta = computed(() => {
  const window = attendanceWindow.value;
  if (!window) return '';

  return t('LEADS.QUICK_VIEW.ATTENDANCE_WINDOW_META', {
    openedAt: formatDate(window.opened_at),
    closesAt: formatDate(window.closes_at),
  });
});

const qualificationAnswerText = question => {
  const answer = question.answer || {};
  const rawValue = answer.answer_text || answer.text || answer.answer_value;
  const value = rawValue?.value ?? rawValue;
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (value && typeof value === 'object') {
    return Object.values(value).filter(Boolean).join(', ');
  }
  return value || t('LEADS.QUICK_VIEW.CRM.EMPTY_VALUE');
};
</script>

<template>
  <LaisDrawer
    :show="show"
    side="right"
    size="lg"
    :title="t('LEADS.QUICK_VIEW.TITLE')"
    @close="emit('close')"
  >
    <template #body>
      <div v-if="lead" class="grid gap-4">
        <section class="flex items-start gap-3">
          <div
            class="grid size-12 shrink-0 place-items-center rounded-full bg-lais-primary-50 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-primary"
          >
            {{ leadInitials(lead) }}
          </div>
          <div class="min-w-0 flex-1">
            <h3
              class="truncate font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
            >
              {{ lead.name || t('LEADS.QUICK_VIEW.ANONYMOUS') }}
            </h3>
            <p class="truncate text-lais-body-sm text-lais-text-muted">
              {{ contactLine || t('LEADS.QUICK_VIEW.NO_CONTACT') }}
            </p>
            <p class="mt-1 text-lais-body-xs text-lais-text-muted">
              {{
                clientSince
                  ? t('LEADS.QUICK_VIEW.CLIENT_SINCE', {
                      date: clientSince,
                    })
                  : t('LEADS.QUICK_VIEW.CLIENT_SINCE_EMPTY')
              }}
            </p>
            <div class="mt-2 flex flex-wrap gap-2">
              <StatusTag
                :label="stageLabel"
                :variant="stageMeta.variant"
                size="xs"
              />
              <BuyerProfileTag
                :profile="profile"
                :business-context="leadBusinessContext"
              />
            </div>
          </div>
        </section>

        <LaisCard density="tight">
          <dl class="grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
            <div
              v-for="card in activityCards"
              :key="card.label"
              class="rounded-lais-sm border border-lais-border-soft px-3 py-3"
            >
              <dt
                class="grid justify-items-center gap-1 text-lais-label-xs uppercase text-lais-text-muted"
              >
                <span class="size-4 text-lais-primary" :class="card.icon" />
                {{ card.label }}
              </dt>
              <dd
                class="mt-2 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
              >
                {{ card.value }}
              </dd>
            </div>
          </dl>
          <p class="mt-3 text-center text-lais-body-xs text-lais-text-muted">
            {{ `${t('LEADS.QUICK_VIEW.LAST_ACTIVITY')}:` }}
            {{ lastActivity || t('LEADS.QUICK_VIEW.NO_ACTIVITY') }}
          </p>
        </LaisCard>

        <LaisCard
          v-if="lead.attendance_stage_label || lead.qualification_status"
          density="tight"
        >
          <p class="text-lais-label-sm uppercase text-lais-text-muted">
            {{ t('LEADS.QUICK_VIEW.CRM_STAGE') }}
          </p>
          <p class="mt-1 font-semibold text-lais-text">
            {{ lead.attendance_stage_label || stageLabel }}
          </p>
          <p class="mt-1 text-lais-body-sm text-lais-text-muted">
            {{ stageDescription }}
          </p>
        </LaisCard>

        <LaisCard v-if="attendanceWindow" density="tight">
          <p class="text-lais-label-sm uppercase text-lais-text-muted">
            {{ t('LEADS.QUICK_VIEW.ATTENDANCE_WINDOW_TITLE') }}
          </p>
          <p class="mt-1 font-semibold text-lais-text">
            {{ attendanceWindowStatus }}
          </p>
          <p class="mt-1 text-lais-body-sm text-lais-text-muted">
            {{ attendanceWindowMeta }}
          </p>
          <p
            v-if="attendanceWindow.invalid_reason"
            class="mt-2 text-lais-body-sm font-semibold text-amber-700"
          >
            {{
              t('LEADS.QUICK_VIEW.ATTENDANCE_WINDOW_REASON', {
                reason: attendanceWindow.invalid_reason,
              })
            }}
          </p>
        </LaisCard>

        <LaisCard
          v-if="
            qualificationQuestionItems.length ||
            pendingQualifyingQuestions.length
          "
          density="tight"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-lais-label-sm uppercase text-lais-text-muted">
                {{ t('LEADS.QUICK_VIEW.QUALIFICATION.TITLE') }}
              </p>
              <p class="mt-1 text-lais-body-sm text-lais-text-muted">
                {{
                  pendingQualifyingQuestions.length
                    ? t(
                        'LEADS.QUICK_VIEW.QUALIFICATION.PENDING_COUNT',
                        pendingQualifyingQuestions.length,
                        { n: pendingQualifyingQuestions.length }
                      )
                    : t('LEADS.QUICK_VIEW.QUALIFICATION.READY')
                }}
              </p>
            </div>
            <span
              class="inline-flex rounded-full px-2 py-1 text-lais-label-xs font-semibold"
              :class="
                pendingQualifyingQuestions.length
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-emerald-50 text-emerald-700'
              "
            >
              {{
                qualificationQuestions.crm_ready
                  ? t('LEADS.QUICK_VIEW.QUALIFICATION.COMPLETE')
                  : t('LEADS.QUICK_VIEW.QUALIFICATION.PENDING')
              }}
            </span>
          </div>
          <dl class="mt-3 grid gap-2 text-lais-body-sm">
            <div
              v-for="question in qualificationQuestionItems.slice(0, 5)"
              :key="question.id || question.key"
              class="grid gap-1 rounded-lais-sm border border-lais-border-soft p-2"
            >
              <dt class="font-semibold text-lais-text">
                {{ question.label }}
              </dt>
              <dd
                :class="
                  question.pending
                    ? 'font-semibold text-amber-700'
                    : 'text-lais-text-muted'
                "
              >
                {{
                  question.pending
                    ? t('LEADS.QUICK_VIEW.QUALIFICATION.PENDING')
                    : qualificationAnswerText(question)
                }}
              </dd>
            </div>
          </dl>
        </LaisCard>

        <LaisCard density="tight">
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-lais-label-sm uppercase text-lais-text-muted">
                {{ t('LEADS.QUICK_VIEW.CRM.TITLE') }}
              </p>
              <span
                class="mt-2 inline-flex rounded-full px-2 py-1 text-lais-label-xs font-semibold"
                :class="crmStatusMeta.cls"
              >
                {{ crmStatusLabel }}
              </span>
            </div>
            <button
              type="button"
              class="inline-flex h-8 items-center gap-1.5 rounded-lais-sm border border-lais-border-soft px-3 text-lais-label-xs font-semibold text-lais-primary hover:border-lais-primary disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="isCrmSyncing"
              @click="emit('sync-crm', lead.id)"
            >
              <span
                :class="
                  isCrmSyncing
                    ? 'i-lucide-loader-circle animate-spin'
                    : 'i-lucide-refresh-cw'
                "
                class="size-3.5"
                aria-hidden="true"
              />
              {{
                isCrmSyncing
                  ? t('LEADS.QUICK_VIEW.CRM.RESENDING')
                  : t('LEADS.QUICK_VIEW.CRM.RESEND')
              }}
            </button>
          </div>
          <div
            v-if="hasIndicationBypass"
            class="mt-3 rounded-lais-sm border border-sky-100 bg-sky-50 px-3 py-2 text-lais-body-sm font-semibold text-sky-700"
          >
            {{ crmBypassNotice }}
          </div>
          <dl class="mt-3 grid gap-2 text-lais-body-sm">
            <div class="flex justify-between gap-3">
              <dt class="text-lais-text-muted">
                {{ t('LEADS.QUICK_VIEW.CRM.ATTEMPT') }}
              </dt>
              <dd class="font-semibold text-lais-text">
                {{ latestCrmAttemptLabel }}
              </dd>
            </div>
            <div class="flex justify-between gap-3">
              <dt class="text-lais-text-muted">
                {{ t('LEADS.QUICK_VIEW.CRM.LAST_SYNC') }}
              </dt>
              <dd class="font-semibold text-lais-text">
                {{
                  formatDate(crmSync.last_synced_at) ||
                  t('LEADS.QUICK_VIEW.CRM.EMPTY_VALUE')
                }}
              </dd>
            </div>
            <div v-if="crmSync.last_error" class="grid gap-1">
              <dt class="text-lais-text-muted">
                {{ t('LEADS.QUICK_VIEW.CRM.ERROR') }}
              </dt>
              <dd class="text-[#b42318]">{{ crmSync.last_error }}</dd>
            </div>
            <div
              v-if="contactability.submitted_phone_number"
              class="grid gap-1"
            >
              <dt class="text-lais-text-muted">
                {{ t('LEADS.QUICK_VIEW.CRM.SUBMITTED_PHONE') }}
              </dt>
              <dd class="font-semibold text-lais-text">
                {{ contactability.submitted_phone_number }}
              </dd>
            </div>
            <div v-if="contactability.whatsapp_phone_number" class="grid gap-1">
              <dt class="text-lais-text-muted">
                {{ t('LEADS.QUICK_VIEW.CRM.WHATSAPP_PHONE') }}
              </dt>
              <dd class="font-semibold text-lais-text">
                {{ contactability.whatsapp_phone_number }}
              </dd>
            </div>
          </dl>
          <details v-if="latestCrmAttempt" class="mt-3">
            <summary
              class="cursor-pointer text-lais-label-xs font-semibold text-lais-primary"
            >
              {{ t('LEADS.QUICK_VIEW.CRM.PAYLOAD_RESPONSE') }}
            </summary>
            <textarea
              readonly
              class="mt-2 h-52 w-full resize-none overflow-auto rounded-lais-sm bg-lais-bg-soft p-3 text-[11px] text-lais-text"
              :value="latestCrmAttemptJson"
            />
          </details>
        </LaisCard>

        <VisitorInfoHub
          v-if="lead.id"
          :visitor-id="lead.id"
          :lead="lead"
          :business-context="leadBusinessContext"
          embedded
          @schedule-visit="emit('schedule-visit', lead)"
        />
      </div>
      <div v-else class="grid min-h-[220px] place-items-center text-center">
        <div class="grid max-w-sm justify-items-center gap-2">
          <span
            class="i-lucide-user-round-search size-8 text-lais-text-muted"
            aria-hidden="true"
          />
          <h3 class="font-redHatDisplay text-lais-heading-sm text-lais-text">
            {{ t('LEADS.QUICK_VIEW.EMPTY_TITLE') }}
          </h3>
          <p class="text-lais-body-sm text-lais-text-muted">
            {{ t('LEADS.QUICK_VIEW.EMPTY_DESCRIPTION') }}
          </p>
        </div>
      </div>
    </template>

    <template #footer>
      <div v-if="lead" class="grid gap-2">
        <div class="grid grid-cols-3 gap-2">
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border-soft px-3 text-lais-label-sm font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
            @click="emit('message', lead)"
          >
            <span class="i-lucide-message-circle size-4" aria-hidden="true" />
            {{ t('LEADS.QUICK_VIEW.MESSAGE') }}
          </button>
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border-soft px-3 text-lais-label-sm font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
            @click="emit('call', lead)"
          >
            <span class="i-lucide-phone size-4" aria-hidden="true" />
            {{ t('LEADS.QUICK_VIEW.CALL') }}
          </button>
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border-soft px-3 text-lais-label-sm font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
            @click="emit('schedule-visit', lead)"
          >
            <span class="i-lucide-calendar-plus size-4" aria-hidden="true" />
            {{ t('LEADS.QUICK_VIEW.NEW_VISIT') }}
          </button>
        </div>
        <button
          type="button"
          class="inline-flex h-10 items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 text-lais-label-sm font-semibold text-white transition hover:bg-lais-primary-600"
          @click="emit('open-history', lead)"
        >
          <span class="i-lucide-user-round-search size-4" aria-hidden="true" />
          {{ t('LEADS.QUICK_VIEW.OPEN_HISTORY') }}
        </button>
        <div class="grid gap-2">
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center gap-2 rounded-lais border border-lais-border-soft px-3 text-lais-label-sm font-semibold text-lais-text transition hover:border-lais-primary hover:text-lais-primary"
            @click="emit('create-referral', lead)"
          >
            <span class="i-lucide-forward size-4" aria-hidden="true" />
            {{ t('LEADS.QUICK_VIEW.REFER') }}
          </button>
        </div>
      </div>
    </template>
  </LaisDrawer>
</template>
